import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  Box,
} from "@mui/material";
import { Search, Download, TableChartOutlined } from "@mui/icons-material";
import * as XLSX from "xlsx";
import Apiurl from "../../Helpers/apiUrl";
import { ClickableTableLink } from "../admin/ClickableTableLink";
import { useNavigate } from "react-router-dom";
import { roleOpt, FILTER_BAR_OPTIONS } from "../admin/admin_dashboard/dashConstants";

const headerStyle = {
  fontWeight: 700,
  fontSize: "11px",
  color: "#334155 !important",
  bgcolor: "#f1f5f9 !important",
  py: 1.5,
  px: 2.5,
  letterSpacing: "0.05em",
  borderBottom: "2px solid #cbd5e1",
  borderRight: "1px solid #cbd5e1",
  "&:last-child": { borderRight: "none" },
  zIndex: 10,
  textTransform: "uppercase"
};

const bodyStyle = {
  fontSize: "13px",
  py: 1.25,
  px: 2.5,
  color: "#334155",
  borderBottom: "1px solid #cbd5e1",
  borderRight: "1px solid #cbd5e1",
  "&:last-child": { borderRight: "none" },
};

const ROLE_NAMES = {
  2: "IC", 3: "BSP", 4: "DSP", 5: "BD", 6: "Sales Enabler", 7: "Dealer", 8: "Studio",
};

// Refined enterprise badges
const ROLE_BADGE_CLASSES = {
  2: "text-blue-700 bg-blue-50 border-blue-200/80",
  3: "text-indigo-700 bg-indigo-50 border-indigo-200/80",
  4: "text-slate-700 bg-slate-50 border-slate-200",
  5: "text-cyan-700 bg-cyan-50 border-cyan-200/80",
  6: "text-sky-700 bg-sky-50 border-sky-200/80",
  7: "text-blue-900 bg-blue-100 border-blue-300/80",
  8: "text-slate-800 bg-slate-100 border-slate-300",
};

const RoleWiseQuotation = ({ payload }) => {

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const token = localStorage.getItem("u_token");

  const { data, isError } = useQuery({
    queryKey: ["roleQuotations", searchTerm, page, rowsPerPage, payload],
    queryFn: async () => {
      const response = await fetch(
        `${Apiurl}admin/dashboard-role-quotaion?${new URLSearchParams({
          search: searchTerm,
          page: page + 1,
          limit: rowsPerPage === -1 ? totalCount || 100000 : rowsPerPage,
          userId: payload.user_id ? (Array.isArray(payload.user_id) ? payload.user_id.join(",") : payload.user_id) : null,
          month: payload.month, year: payload.year, from: payload.from, to: payload.to, role: payload.role, region: payload.region, state: payload.state,
        })}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
    placeholderData: (previousData) => previousData,
  });

  const rowData = data?.data || [];
  const totalCount = data?.total || 0;

  const handleExport = () => {
    const exportData = rowData.map((item, index) => ({
      "Sl No.": page * rowsPerPage + index + 1,
      Name: `${item.name} ${item.last_name || ""}`.trim(),
      "Emp Code": item.emp_code,
      Role: ROLE_NAMES[item.role_id] || "N/A",
      "City/Region": item.city,
      State: item.state,
      "Total Projects": item.total_projects,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Quotation Report");
    XLSX.writeFile(workbook, "UserWise_Quotation_Report.xlsx");
  };

  return (
    <Box
      className="flex flex-col bg-white transition-all duration-300"
      sx={{
        borderRadius: "14px",
        overflow: "hidden",
        border: "1px solid #cbd5e1",
        boxShadow: "0 4px 24px -6px rgba(148, 163, 184, 0.12)",
        height: "calc(100vh - 120px)",
      }}
    >
      {/* Upper Utility Action Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-slate-50 border-b border-slate-300">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-white border border-slate-200 shadow-sm">
            <TableChartOutlined style={{ fontSize: "1.1rem", color: "#475569" }} />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-800 tracking-tight">
              User-Wise Quotations
            </h3>
            <p className="text-[11px] font-medium text-slate-500">
              Overview of regional transactions and project logs
            </p>
          </div>
        </div>

        {/* Action Controllers */}
        <div className="flex items-center gap-2 sm:w-auto w-full">
          <TextField
            placeholder="Search code or name..."
            size="small"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 16, color: "#64748b" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "8px",
                bgcolor: "#ffffff",
                fontSize: "12px",
                height: "34px",
                width: { xs: "100%", sm: "220px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
              },
            }}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<Download style={{ fontSize: "1rem" }} />}
            onClick={handleExport}
            sx={{
              height: "34px",
              backgroundColor: "#1e293b",
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
              border: "1px solid #0f172a",
              "&:hover": {
                backgroundColor: "#0f172a",
                boxShadow: "none"
              }
            }}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Main Grid Matrix Workspace */}
      <div className="flex-grow overflow-hidden bg-slate-100">
        <TableContainer
          sx={{
            height: "calc(100% - 52px)",
            bgcolor: "#fff",
            "&::-webkit-scrollbar": { width: "6px", height: "6px" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: "10px" },
          }}
        >
          <Table stickyHeader size="small" sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerStyle}>SL NO.</TableCell>
                <TableCell sx={headerStyle}>NAME</TableCell>
                <TableCell sx={headerStyle}>EMP CODE</TableCell>
                <TableCell sx={headerStyle}>ROLE</TableCell>
                <TableCell sx={headerStyle}>REGION</TableCell>
                <TableCell sx={headerStyle}>STATE</TableCell>
                <TableCell align="right" sx={headerStyle}>QUOTATIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rowData.map((row, index) => {
                const isEven = index % 2 === 0;
                return (
                  <TableRow
                    key={index}
                    className={`group transition-colors duration-100 
                      ${isEven ? 'bg-white' : 'bg-slate-50/60'} 
                      hover:bg-slate-100/70`}
                  >
                    <TableCell
                      sx={{
                        ...bodyStyle,
                        fontWeight: 500,
                        color: "#0f172a",
                        fontFamily: "monospace",
                        bgcolor: isEven ? "rgba(241, 245, 249, 0.3)" : "rgba(241, 245, 249, 0.6)",
                        "&.MuiTableCell-root": {
                          transition: "background-color 100ms ease"
                        },
                        ".group:hover &": {
                          bgcolor: "rgba(226, 232, 240, 0.5)"
                        }
                      }}
                    >
                      {(page * rowsPerPage + index + 1)}
                    </TableCell>

                    <TableCell sx={{ ...bodyStyle, fontWeight: 600, color: "#1e293b", textTransform: "capitalize" }}>
                      <ClickableTableLink
                        path="/admin/userlist"
                        displayValue={`${row.name} ${row.last_name}`}
                        searchValue={row.emp_code}
                        date={row.user_created_at ? row.user_created_at.substring(0, 4) : null}
                      />
                    </TableCell>

                    <TableCell sx={bodyStyle}>
                      <span className="text-[11px] font-mono font-bold text-slate-700 bg-slate-100 border border-slate-300 px-1.5 py-0.5 rounded uppercase">
                        {row.emp_code}
                      </span>
                    </TableCell>

                    <TableCell sx={bodyStyle}>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${ROLE_BADGE_CLASSES[row.role_id] || "text-slate-600 bg-white border-slate-300"}`}>
                        {ROLE_NAMES[row.role_id] || "N/A"}
                      </span>
                    </TableCell>

                    <TableCell sx={bodyStyle}>{row.city || "—"}</TableCell>
                    <TableCell sx={bodyStyle}>{row.state || "—"}</TableCell>

                    <TableCell align="right" sx={{ ...bodyStyle, fontWeight: 700, fontFamily: "monospace", color: "#0f172a" }}>
                      <span
                        onClick={() => {
                          let finalFrom = "";
                          let finalTo = "";

                          if (payload?.from || payload?.to) {
                            finalFrom = payload.from || "";
                            finalTo = payload.to || "";
                          }
                          else if (payload?.month && payload?.year) {
                            const yearNum = Number(payload.year);
                            const monthNum = Number(payload.month);

                            finalFrom = `${yearNum}-${String(monthNum).padStart(2, "0")}-01`;

                            const lastDay = new Date(yearNum, monthNum, 0).getDate();
                            finalTo = `${yearNum}-${String(monthNum).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
                          }
                          else if (payload?.year) {
                            finalFrom = `${payload.year}-01-01`;
                            finalTo = `${payload.year}-12-31`;
                          }
                          navigate("/admin/projects", {
                            state: {
                              months: Array.isArray(payload?.month) ? payload.month : payload?.month?.split(",").map(y => y.trim()) || [],
                              regions:payload.region,
                              roles:payload.role,
                              states:payload.state,
                              users: [row.emp_code],
                              years: Array.isArray(payload?.year) ? payload.year : payload?.year?.split(",").map(y => y.trim()) || []
                            }
                          });
                        }}
                        className="inline-block px-2 py-0.5 bg-slate-100 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 transition-colors cursor-pointer rounded border-b border-blue-500"
                      >
                        {row.total_projects}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100, { label: "All", value: -1 }]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage === -1 ? totalCount : rowsPerPage}
          page={page}
          onPageChange={(e, n) => setPage(n)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            borderTop: "1px solid #cbd5e1",
            bgcolor: "#fff",
            ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
              fontSize: "11px",
              fontWeight: 600,
              color: "#475569"
            }
          }}
        />
      </div>
    </Box>
  );
};

export default RoleWiseQuotation;